import { useMemo, useState } from "react";

import clsx from "clsx";

import { TitleSlide } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { Spinner } from "@components/v2";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";
import { MetricValue } from "@shared/metric-value";

import { AttributionOptionsSelect } from "../attribution-options-select";
import {
  type AdoptedMetrics,
  type AdoptedMetricsData,
  type DaySelection,
} from "../types";
import { calculateMeanValueForDaysSelection } from "../utils";

import { MetricModal } from "./metric-modal";

interface Props {
  hideFilters?: boolean;
  adoptedMetrics: AdoptedMetrics[];
  adoptedMetric: AdoptedMetrics;
  adoptedMetricsData?: AdoptedMetricsData;
  daysSelection: DaySelection[];
  adoptableBenchmarksMetrics: SalesPageMetric[];
  benchmarkAttributionModel: string;
  benchmarkAttributionWindow: IMCAttributionWindowDays;
  benchmarkAccountingMode: INBAccountingMode;
  showFtRtnMetrics?: boolean;
  onUpdate(value: AdoptedMetrics): void;
  onDelete(): void;
  onBenchmarkAttributionModelChange(val: string): void;
  onBenchmarkAttributionWindowChange(val: IMCAttributionWindowDays): void;
  onBenchmarkAccountingModeChange(val: INBAccountingMode): void;
}

export const BenchmarkCard = ({
  hideFilters,
  showFtRtnMetrics,
  adoptedMetrics,
  adoptedMetric,
  adoptedMetricsData,
  adoptableBenchmarksMetrics,
  daysSelection,
  benchmarkAttributionModel,
  benchmarkAttributionWindow,
  benchmarkAccountingMode,
  onUpdate,
  onDelete,
  onBenchmarkAttributionModelChange,
  onBenchmarkAttributionWindowChange,
  onBenchmarkAccountingModeChange,
}: Props) => {
  const { user } = useUser();

  const [isEditing, setIsEditing] = useState(false);

  const metric = useMemo(() => {
    return adoptableBenchmarksMetrics.find(
      (e) => e.id === adoptedMetric.metricId,
    );
  }, [adoptedMetric, adoptableBenchmarksMetrics]);

  const metricValue = useMemo(() => {
    return calculateMeanValueForDaysSelection({
      daysSelection,
      metric,
      rows: adoptedMetricsData?.data?.rows,
    });
  }, [adoptedMetricsData, metric, daysSelection]);

  if (!metric) {
    return (
      <div className="relative border border-v2-tertiary rounded-lg w-52 py-4 px-5">
        <TitleSlide className="text-xs text-center">
          Could not find this Traffic Source.
        </TitleSlide>
      </div>
    );
  }

  const isLoading =
    !adoptedMetricsData || adoptedMetricsData.status === "loading";

  const metricName =
    metric.dashboardV2Name ||
    metric.metadata.overviewPageMetricName ||
    metric.metadata.name ||
    metric.name;

  return (
    <div
      className={clsx(
        "border border-v2-secondary rounded-md shadow-[0_2px_0] shadow-v2-black/10% p-4 h-full",
        "flex flex-col justify-between bg-v2-primary",
      )}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Spinner className="text-v2-action" />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1">
            <div className="text-2xl font-semibold truncate">{metricName}</div>
            <div className="shrink-0 flex items-center">
              <GenericTooltip
                noInfoCircle
                content={isLoading ? null : "Configure"}
                overlayClassName="pointer-events-none"
                placement="top"
                mouseEnterDelay={500}
              >
                <button
                  disabled={isLoading}
                  className={clsx(
                    "h-6 w-6 rounded-md flex-center justify-center text-sm",
                    "text-v2-action hover:bg-v2-cosmic/50",
                  )}
                  onClick={() => setIsEditing(true)}
                >
                  <i className="fa-regular fa-filter"></i>
                </button>
              </GenericTooltip>
            </div>
          </div>
          <div
            className={clsx(
              "text-[42px] leading-[42px] text-v2-action-positive font-bold pt-2 pb-3 truncate",
            )}
          >
            <GenericTooltip
              className="truncate"
              noInfoCircle
              mouseEnterDelay={1000}
              placement="top"
              content={
                <MetricValue
                  as="span"
                  format={metric.format}
                  value={metricValue}
                  precision="exact"
                />
              }
            >
              <MetricValue
                className="truncate"
                as="span"
                format={metric.format}
                value={metricValue}
                precision="exact"
              />
            </GenericTooltip>
          </div>
          <AttributionOptionsSelect
            options={user.attributionMethodEnum.reactSelectOptions.filter(
              (e) => !e.value.includes("northbeam_custom_"),
            )}
            attributionModel={benchmarkAttributionModel}
            attributionWindow={benchmarkAttributionWindow}
            accountingMode={benchmarkAccountingMode}
            onAttributionModelChange={onBenchmarkAttributionModelChange}
            onAttributionWindowChange={onBenchmarkAttributionWindowChange}
            onAccountingModeChange={onBenchmarkAccountingModeChange}
          />
        </>
      )}
      <MetricModal
        isOpen={isEditing}
        isDeletable={adoptedMetrics.length > 1}
        hideFilters={hideFilters}
        showFtRtnMetrics={showFtRtnMetrics}
        adoptableMetrics={adoptableBenchmarksMetrics}
        adoptedMetrics={adoptedMetrics}
        initialMetricId={adoptedMetric.metricId}
        initialFilters={adoptedMetric.benchmarkFilters}
        onClose={() => {
          setIsEditing(false);
        }}
        onSubmit={(value) => {
          onUpdate({
            ...adoptedMetric,
            metricId: value.metricId,
            benchmarkFilters: value.filters,
          });
          setIsEditing(false);
        }}
        onDelete={() => {
          setIsEditing(false);
          onDelete();
        }}
      />
    </div>
  );
};
